import React from "react";
import { FONT, T, DEVICES, prefersReducedMotion } from "./tokens";
import BrowserChromeBar from "./BrowserChromeBar";

// ===========================================================================
// PreviewFrame — the framed "browser window" holding the live preview iframe.
//   • device-aware width (desktop fills, tablet/phone are centered + framed)
//   • faux browser chrome (BrowserChromeBar)
//   • a floating slot (`toolbar`) docked top-center — the LIVEWIRE toolbar
//   • a header-right slot for the health chip
//   • a loading veil and a publish "burst" layer
//
// The iframe itself is controlled by the parent (ref + src + onLoad) so the
// SectionBridge can pair with it.
// ===========================================================================

export default function PreviewFrame({
  iframeRef,
  src,
  title = "Live site preview",
  device = "desktop",
  loading,
  onLoad,
  onReload,
  displayUrl,
  toolbar,
  headerRight,
  burst,
  children,
}) {
  const preset = DEVICES.find((d) => d.id === device) || DEVICES[0];
  const framed = !!preset.w; // tablet / phone are visibly framed
  const reduce = prefersReducedMotion();

  return (
    <div
      id="showroom-frame"
      style={{
        position: "relative",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: framed ? "flex-start" : "stretch",
        padding: framed ? "28px 20px" : 14,
        overflow: "auto",
      }}
    >
      {/* Floating LIVEWIRE toolbar — docked top-center of the frame. */}
      {toolbar && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 30,
          }}
        >
          {toolbar}
        </div>
      )}

      {/* Health chip — top-right of the frame header region. */}
      {headerRight && (
        <div style={{ position: "absolute", top: 18, right: 20, zIndex: 30 }}>{headerRight}</div>
      )}

      {/* The browser window. */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: framed ? preset.w : "100%",
          maxWidth: "100%",
          height: framed ? "auto" : "100%",
          minHeight: 0,
          flex: framed ? "0 0 auto" : 1,
          borderRadius: T.radiusFrame,
          overflow: "hidden",
          background: "#fff",
          border: `1px solid ${T.line}`,
          boxShadow: T.shadowLg,
          transition: reduce ? "none" : `width var(--ds-dur-slow) ${T.ease}`,
        }}
      >
        <BrowserChromeBar url={displayUrl} onReload={onReload} />

        <div style={{ position: "relative", flex: 1, minHeight: framed ? 520 : 0 }}>
          <iframe
            ref={iframeRef}
            src={src}
            title={title}
            onLoad={onLoad}
            style={{ width: "100%", height: "100%", minHeight: framed ? 520 : 0, border: "none", display: "block", background: "#fff" }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />

          {/* Loading veil */}
          {loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff",
                zIndex: 3,
                fontFamily: FONT,
                fontSize: 13,
                color: T.ink3,
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  border: `2px solid ${T.line2}`,
                  borderTopColor: T.blue,
                  borderRadius: "50%",
                  animation: reduce ? "none" : "spin .7s linear infinite",
                  marginRight: 10,
                }}
              />
              Loading preview…
            </div>
          )}

          {children}
        </div>
      </div>

      {/* Publish burst — celebratory ring over the frame center. */}
      {burst && !reduce && (
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span
            style={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              border: `3px solid ${T.blue}`,
              animation: "showroomBurst 0.7s ease-out forwards",
            }}
          />
        </div>
      )}
    </div>
  );
}
